/**
 * 정지 API 오류 → 화면 문구·후속 동작.
 * 오류 객체는 공용 `apiClient`(src/api/client.ts)가 던지는 `{ status, serverMessage, body: { errorCode, details } }`.
 */
import { PauseApiErrorCode } from './types';

export type PauseErrorKind =
    | 'stale'
    | 'notInUse'
    | 'startAfterExpiry'
    | 'keyReused'
    | 'invalid'
    | 'forbidden'
    | 'permissionOff'
    | 'notFound'
    | 'unsupported'
    | 'network'
    | 'unknown';

export type ResolvedPauseError = {
    kind: PauseErrorKind;
    title: string;
    message: string;
    /** 상세를 다시 불러와야 하는가 */
    refetch: boolean;
    /** 지금 요청 키를 버려야 하는가(같은 키로 다시 보내면 안 되는 경우) */
    dropKey: boolean;
};

type ErrorLike = {
    status?: number;
    response?: { status?: number };
    serverMessage?: string;
    message?: string;
    body?: { errorCode?: string; message?: string; details?: unknown } | null;
};

const STALE_CODES: PauseApiErrorCode[] = [
    'MEMBERSHIP_PAUSE_REVISION_MISMATCH',
    'MEMBERSHIP_PAUSE_ALREADY_OPEN',
    'MEMBERSHIP_PAUSE_NOT_OPEN',
    'MEMBERSHIP_PAUSE_CONFLICT',
];

/**
 * 구서버(정지 API 가 없는 서버·롤백 빌드)의 404 인가.
 * 신서버도 수강권이 없으면 404 를 내므로 상태 코드만으로는 못 가른다 — 서버 공통 404 는
 * `errorCode` 없이 `message: 'Not found'` 다(rappo `src/app.js` 마지막 처리기).
 */
export const isLegacyServerNotFound = (error: unknown) => {
    const e = (error || {}) as ErrorLike;
    if ((e.status ?? e.response?.status) !== 404) return false;
    if (e.body?.errorCode) return false;
    const message = String(e.body?.message ?? e.serverMessage ?? '').trim();
    return message === 'Not found';
};

const serverText = (e: ErrorLike, fallback: string) => {
    const text = String(e.body?.message ?? e.serverMessage ?? '').trim();
    return text || fallback;
};

export const resolvePauseError = (error: unknown): ResolvedPauseError => {
    const e = (error || {}) as ErrorLike;
    // 정규화 전 raw HTTPError 는 상태가 response 에만 있다 — 네트워크(상태 없음)로 오판하지 않게 함께 읽는다.
    const status = typeof e.status === 'number' ? e.status : typeof e.response?.status === 'number' ? e.response.status : 0;
    const code = String(e.body?.errorCode ?? '') as PauseApiErrorCode | '';

    if (!status) {
        return {
            kind: 'network',
            title: '결과 확인 실패',
            message: '결과를 확인하지 못했어요.\n다시 누르면 같은 요청으로 처리돼요.',
            refetch: false,
            dropKey: false,
        };
    }
    if (status === 409 && code && STALE_CODES.includes(code)) {
        return {
            kind: 'stale',
            title: '정지 정보가 바뀌었어요',
            message: '다른 곳에서 정지 정보가 바뀌었어요.\n바뀐 내용을 확인하고 다시 눌러 주세요.',
            refetch: true,
            dropKey: true,
        };
    }
    if (code === 'MEMBERSHIP_PAUSE_PASS_NOT_IN_USE') {
        return {
            kind: 'notInUse',
            title: '정지할 수 없어요',
            message: '사용 중인 수강권만 정지하거나 해제할 수 있어요.',
            refetch: true,
            dropKey: true,
        };
    }
    if (code === 'MEMBERSHIP_PAUSE_START_AFTER_EXPIRY') {
        return {
            kind: 'startAfterExpiry',
            title: '시작일 확인',
            message: serverText(e, '시작일이 만료일보다 늦어요.'),
            refetch: false,
            dropKey: true,
        };
    }
    if (code === 'MEMBERSHIP_PAUSE_REQUEST_KEY_REUSED' || code === 'MEMBERSHIP_PAUSE_INVALID_REQUEST_KEY') {
        return {
            kind: 'keyReused',
            title: '다시 시도해 주세요',
            message: '요청을 처리하지 못했어요. 다시 눌러 주세요.',
            refetch: false,
            dropKey: true,
        };
    }
    if (status === 400) {
        return {
            kind: 'invalid',
            title: '입력 확인',
            message: serverText(e, '날짜를 다시 확인해 주세요.'),
            refetch: false,
            dropKey: true,
        };
    }
    if (status === 412) {
        return {
            kind: 'permissionOff',
            title: '권한 없음',
            message: '수강권 관리 권한이 꺼져 있어요.',
            refetch: false,
            dropKey: true,
        };
    }
    if (status === 403) {
        return {
            kind: 'forbidden',
            title: '권한 없음',
            message: '수강권을 정지할 권한이 없어요.',
            refetch: false,
            dropKey: true,
        };
    }
    if (status === 404 && isLegacyServerNotFound(e)) {
        return {
            kind: 'unsupported',
            title: '정지 기능 사용 불가',
            message: '지금은 정지 기능을 쓸 수 없어요.',
            refetch: false,
            dropKey: true,
        };
    }
    if (status === 404) {
        return {
            kind: 'notFound',
            title: '수강권 확인',
            message: '수강권을 찾을 수 없어요. 새로고침 후 다시 확인해 주세요.',
            refetch: true,
            dropKey: true,
        };
    }
    // 5xx 등: 서버가 썼는지 모른다 → 같은 키로 다시 보내야 안전하다.
    return {
        kind: 'unknown',
        title: '요청 실패',
        message: `요청을 처리하지 못했어요. 다시 눌러 주세요. (오류 코드 ${status})`,
        refetch: false,
        dropKey: false,
    };
};

/**
 * 정지 API 경계에서 오류 모양을 하나로 맞춘다.
 * 공용 apiClient 는 `{ status, serverMessage, body }` 를 던지지만, 토큰 갱신 뒤 재요청(`ky(request)`)은
 * 그 처리를 벗어나 ky 의 HTTPError(상태는 `response.status`)를 그대로 던진다(교차검증 6).
 * 그 경우 본문을 읽어 errorCode 까지 살린다. 상태가 이미 있거나 응답이 없는 오류(네트워크)는 그대로 둔다.
 */
export const normalizePauseApiError = async (error: unknown): Promise<unknown> => {
    const e = error as { status?: unknown; message?: string; response?: { status?: number; clone?: () => { json: () => Promise<any> } } };
    if (!e || typeof e !== 'object' || typeof e.status === 'number') return error;
    const status = e.response?.status;
    if (typeof status !== 'number') return error;
    let body: any = null;
    try {
        body = (await e.response?.clone?.().json()) ?? null;
    } catch {
        body = null;
    }
    const normalized = new Error(String(body?.message || e.message || '')) as Error & {
        status: number;
        serverMessage: string;
        body: any;
        cause?: unknown;
    };
    normalized.status = status;
    normalized.serverMessage = String(body?.message || '');
    normalized.body = body;
    normalized.cause = error;
    return normalized;
};
